import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Trophy, Star, EyeOff, Eye } from "lucide-react";
import { PageShell } from "../components/PageShell";
import { ClayButton } from "../components/ClayButton";
import { fetchRanking, setLeaderboardOptOut } from "../lib/ranking";

const PODIUM_STYLES = [
  {
    // 1st
    bg: "bg-gradient-to-br from-[#FFD700] to-[#FF9500]",
    ring: "ring-[#FFD700]",
    label: "1°",
    height: "h-40 sm:h-48",
    icon: "🥇",
    order: "sm:order-2",
  },
  {
    // 2nd
    bg: "bg-gradient-to-br from-[#D0D0D0] to-[#9A9A9A]",
    ring: "ring-[#B0B0B0]",
    label: "2°",
    height: "h-32 sm:h-40",
    icon: "🥈",
    order: "sm:order-1",
  },
  {
    // 3rd
    bg: "bg-gradient-to-br from-[#CD7F32] to-[#8B4513]",
    ring: "ring-[#CD7F32]",
    label: "3°",
    height: "h-28 sm:h-36",
    icon: "🥉",
    order: "sm:order-3",
  },
];

function PodiumCard({ entry, rank }) {
  const style = PODIUM_STYLES[rank - 1];
  return (
    <div
      className={`flex flex-col items-center flex-1 ${style.order}`}
      data-testid={`ranking-podium-${rank}`}
    >
      <div
        className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white ring-4 ${style.ring} flex items-center justify-center text-3xl sm:text-4xl mb-2 shadow-md ${
          entry.is_me ? "ring-8" : ""
        }`}
      >
        {entry.avatar}
      </div>
      <p className="font-heading font-bold text-sm sm:text-base text-[#1C1C1E] truncate max-w-full px-1">
        {entry.name} {entry.is_me && <span className="text-xs">(tú)</span>}
      </p>
      <p className="text-xs text-[#5C5C60] mb-2 flex items-center gap-1">
        <Star className="w-3 h-3 fill-[#FFD700] text-[#FFD700]" strokeWidth={2} />
        {entry.points.toLocaleString("es-MX")} pts
      </p>
      <div
        className={`${style.bg} ${style.height} w-full rounded-t-2xl flex flex-col items-center justify-start pt-2 sm:pt-3`}
      >
        <span className="text-3xl sm:text-4xl">{style.icon}</span>
        <span className="font-heading text-2xl sm:text-3xl font-bold text-white mt-1">
          {style.label}
        </span>
      </div>
    </div>
  );
}

function RankRow({ entry, rank }) {
  return (
    <li
      className={`flex items-center gap-3 py-3 px-3 rounded-2xl ${
        entry.is_me
          ? "bg-[#FFF6CC] border-2 border-[#FFD700]"
          : "bg-white border-2 border-transparent"
      }`}
      data-testid={`ranking-row-${rank}`}
    >
      <span className="font-heading font-bold text-lg text-[#5C5C60] w-8 text-center shrink-0">
        {rank}
      </span>
      <span className="text-2xl shrink-0">{entry.avatar}</span>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-[#1C1C1E] truncate">
          {entry.name}{" "}
          {entry.is_me && (
            <span className="text-xs text-[#7A5C00] font-bold">(tú)</span>
          )}
        </p>
      </div>
      <p className="font-heading font-bold text-[#1C1C1E] shrink-0 flex items-center gap-1">
        <Star
          className="w-4 h-4 fill-[#FFD700] text-[#FFD700]"
          strokeWidth={2}
        />
        {entry.points.toLocaleString("es-MX")}
      </p>
    </li>
  );
}

export default function Ranking() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("weekly");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toggling, setToggling] = useState(false);

  const load = async (p = period) => {
    setLoading(true);
    setError(null);
    try {
      const d = await fetchRanking(p);
      setData(d);
    } catch (e) {
      setError(e?.response?.status === 401 ? "auth" : "network");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(period);
  }, [period]);

  const handleToggleOptOut = async () => {
    if (!data) return;
    setToggling(true);
    try {
      await setLeaderboardOptOut(!data.me.opt_out);
      await load(period);
    } catch {
      /* ignore */
    } finally {
      setToggling(false);
    }
  };

  const top = data?.top || [];
  const podium = top.slice(0, 3);
  const rest = top.slice(3);
  const inTopTwenty = data?.me?.rank && data.me.rank <= 20;

  return (
    <PageShell>
      <div className="mt-4">
        <ClayButton
          variant="ghost"
          onClick={() => navigate("/dashboard")}
          data-testid="ranking-back-btn"
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={3} />
          Volver
        </ClayButton>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-3xl mx-auto mt-4 pb-24"
      >
        <div className="flex items-center gap-3 mb-2">
          <Trophy
            className="w-8 h-8 text-[#FFD700] fill-[#FFD700]"
            strokeWidth={2}
          />
          <h1
            className="font-heading text-3xl sm:text-4xl font-bold text-[#1C1C1E]"
            data-testid="ranking-title"
          >
            Ranking
          </h1>
        </div>
        <p className="text-sm text-[#5C5C60] mb-5">
          Los que más puntos han ganado. ¡Sigue jugando para subir! 🚀
        </p>

        {/* Period tabs */}
        <div
          className="inline-flex bg-white rounded-full border-2 border-[#FFEEDC] p-1 mb-6"
          data-testid="ranking-period-tabs"
        >
          <button
            onClick={() => setPeriod("weekly")}
            className={`px-4 sm:px-5 py-2 rounded-full font-bold text-sm transition-colors ${
              period === "weekly"
                ? "bg-[#FF9500] text-white shadow"
                : "text-[#5C5C60] hover:text-[#1C1C1E]"
            }`}
            data-testid="ranking-tab-weekly"
          >
            Esta semana
          </button>
          <button
            onClick={() => setPeriod("all")}
            className={`px-4 sm:px-5 py-2 rounded-full font-bold text-sm transition-colors ${
              period === "all"
                ? "bg-[#FF9500] text-white shadow"
                : "text-[#5C5C60] hover:text-[#1C1C1E]"
            }`}
            data-testid="ranking-tab-all"
          >
            De siempre
          </button>
        </div>

        {loading && (
          <p className="text-[#5C5C60]" data-testid="ranking-loading">
            Cargando ranking…
          </p>
        )}

        {error === "network" && (
          <div className="bg-[#FFEAEA] border-2 border-[#FF6B6B] text-[#803535] rounded-3xl p-5">
            <p className="font-bold">No se pudo cargar el ranking</p>
            <p className="text-sm">Intenta de nuevo en un momento.</p>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {top.length === 0 && (
              <div
                className="bg-white rounded-3xl border-4 border-white shadow-md p-6 text-center"
                data-testid="ranking-empty"
              >
                <p className="text-4xl mb-2">🏁</p>
                <p className="font-heading text-xl font-bold text-[#1C1C1E] mb-1">
                  Aún nadie en el ranking
                </p>
                <p className="text-sm text-[#5C5C60]">
                  {period === "weekly"
                    ? "Sé el primero en marcar puntos esta semana."
                    : "Sé el primero en aparecer en el ranking."}
                </p>
              </div>
            )}

            {/* Podium */}
            {podium.length > 0 && (
              <div
                className="flex flex-row items-end gap-2 sm:gap-4 mb-6"
                data-testid="ranking-podium"
              >
                {podium.map((entry, i) => (
                  <PodiumCard key={entry.id} entry={entry} rank={i + 1} />
                ))}
              </div>
            )}

            {/* Rest of the top 20 */}
            {rest.length > 0 && (
              <div className="bg-white rounded-3xl border-4 border-white shadow-md p-3 sm:p-4 mb-6">
                <ul className="flex flex-col gap-1" data-testid="ranking-list">
                  {rest.map((entry, i) => (
                    <RankRow key={entry.id} entry={entry} rank={i + 4} />
                  ))}
                </ul>
              </div>
            )}

            {/* Current user card (outside top 20) */}
            {!inTopTwenty && data.me.points > 0 && !data.me.opt_out && (
              <div
                className="bg-[#FFF6CC] border-2 border-[#FFD700] rounded-3xl p-4 mb-6"
                data-testid="ranking-me-card"
              >
                <p className="text-xs font-bold uppercase text-[#7A5C00] mb-2">
                  Tu posición
                </p>
                <RankRow
                  entry={{
                    ...data.me,
                    is_me: true,
                    id: "me",
                  }}
                  rank={data.me.rank || "—"}
                />
              </div>
            )}

            {/* If user has 0 points */}
            {data.me.points === 0 && !data.me.opt_out && (
              <div
                className="bg-white rounded-3xl border-2 border-dashed border-[#FFD700] p-5 mb-6 text-center"
                data-testid="ranking-me-empty"
              >
                <p className="text-sm text-[#5C5C60]">
                  {period === "weekly"
                    ? "Aún no tienes puntos esta semana. ¡Juega una ronda para aparecer!"
                    : "Aún no tienes puntos. ¡Juega tu primera ronda para aparecer!"}
                </p>
              </div>
            )}

            {/* Opt-out control */}
            <div
              className="bg-white rounded-3xl border-4 border-white shadow-md p-5"
              data-testid="ranking-privacy-card"
            >
              <div className="flex items-start gap-3">
                {data.me.opt_out ? (
                  <EyeOff
                    className="w-5 h-5 text-[#5C5C60] shrink-0 mt-0.5"
                    strokeWidth={2.5}
                  />
                ) : (
                  <Eye
                    className="w-5 h-5 text-[#34C759] shrink-0 mt-0.5"
                    strokeWidth={2.5}
                  />
                )}
                <div className="flex-1">
                  <p className="font-bold text-[#1C1C1E] mb-1">
                    {data.me.opt_out
                      ? "Estás oculto del ranking"
                      : "Apareces en el ranking"}
                  </p>
                  <p className="text-xs text-[#5C5C60] mb-3">
                    Los papás o el alumno pueden ocultar su nombre y avatar del
                    tablero público en cualquier momento.
                  </p>
                  <ClayButton
                    variant="secondary"
                    onClick={handleToggleOptOut}
                    disabled={toggling}
                    data-testid="ranking-toggle-optout-btn"
                  >
                    {data.me.opt_out ? "Volver a aparecer" : "Ocultarme"}
                  </ClayButton>
                </div>
              </div>
            </div>
          </>
        )}
      </motion.div>
    </PageShell>
  );
}
